import { Inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of, from } from 'rxjs';
import { tap, switchMap, catchError } from 'rxjs/operators';
import { METHODTYPE_SERVICE, type MethodTypeRepository, MethodType } from '@telespot/protocols/domain';

export interface MethodTypeState {
  methodType: MethodType | null;
  loading: boolean;
  error: string | undefined;
}

const initialState: MethodTypeState = {
  methodType: null,
  loading: true,
  error: undefined,
};

@Injectable()
export class MethodTypeStore extends ComponentStore<MethodTypeState> {
  constructor(@Inject(METHODTYPE_SERVICE) private readonly methodTypeRepo: MethodTypeRepository) {
    super(initialState);
  }

  // #region Selectors

  public readonly methodType$ = this.select((state) => state.methodType);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);

  // #endregion

  // #region Updaters

  public readonly setMethodType = this.updater((state, methodType: MethodType | null) => ({
    ...state,
    methodType,
  }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  public readonly setError = this.updater((state, error: string) => ({
    ...state,
    error,
  }));

  public readonly clearError = this.updater((state) => ({
    ...state,
    error: undefined,
  }));

  // #endregion

  // #region Effects

  public readonly findForSample = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      tap(() => {
        this.setLoading(true);
        this.clearError();
      }),
      switchMap((sampleId: string) => {
        return from(this.methodTypeRepo.findForSample(sampleId)).pipe(
          tap((methodType: MethodType | null) => {
            this.setMethodType(methodType);
            this.setLoading(false);
            this.clearError();
          }),
          catchError((error: string) => {
            this.setError(error);
            this.setLoading(false);
            return of(null);
          })
        );
      })
    )
  );

  // #endregion
}
